import { useEffect, useRef, useState } from "react";
import { Button, Col, Container, Row } from "react-bootstrap";
import { generatePath, useNavigate, useParams } from "react-router-dom";
import { useGetFeedbackStepDetailQuery, useSubmitReportMutation } from "store/services/clients/assessmentTreatmentPlan";
import { TREATMENT_SUMMARY_REPORT } from "config/paths";
import RenderAnswer, { RenderAnswerByQId } from "components/shared/ReportComponents/AnswerFields/Index";
import LoadingIndicator from "components/shared/LoadingIndicator";
import ErrorComponent from "components/shared/ErrorComponent";
import LoadingOverlay from "components/shared/LoadingOverlay";
import ClientDetails from "components/shared/ReportComponents/ClientDetails";
import { CLIENT_REPORT_TYPES, TREATMENT_SUMMARY_REPORT_QUESTIONS } from "config/constants";
import { convertToMomentDate, getPdfBlob, successMessageToast } from "helpers/common";
import LoadingButton from "components/shared/LoadingButton";
import KhanReportLogo from "components/shared/ReportComponents/KhanReortLogo";
import TreatmentSummaryGoalsPreview from "components/shared/ReportComponents/TreatmentSummary/TreatmentSummaryGoalsPreview";
import HeaderPreview from "components/shared/ReportComponents/ReportHeader/Preview";
import { clientListingPath } from "helpers/navigationHelper";
import { REPORT_SUBMITTED_SUCCESS } from "config/messages";
import { TreatmentSummaryReportPdfDoc } from "components/PDF/Clients/Reports/TreatmentSummaryReportPdf";
import { useGraphRef } from "components/shared/ReportComponents/TreatmentSummary/GraphRefContext";
import { useUploadFileMutation } from "store/services/utility";
import { store } from "store/store";
import TherapistSignature from "components/shared/ReportComponents/TherapistSignature";
import SaveForLaterBtn from "components/shared/ReportComponents/SaveForLaterBtn";
import moment from "moment";

const TreatmentSummaryReportPreview = () => {

  const isVeteran = store.getState()?.auth?.user?.role_type === "veteran";

  const graphRef = useGraphRef();
  const chartRefs = useRef([]);
  const navigate = useNavigate();
  const { assessmentId, clientId, clientType } = useParams();

  const [isSubmitting, setSubmitting] = useState(false);
  const [isSaveLater, setSaveLater] = useState(false);

  const [submitReport, { isLoading: reportSubmitting }] = useSubmitReportMutation();
  const [uploadFile, { isLoading: uploadingReportPdf }] = useUploadFileMutation();

  const { data, isLoading, isFetching, isError, refetch } = useGetFeedbackStepDetailQuery({ assessmentId, clientId, clientType });
  const assessmentGoals = data?.assesmentData?.reportGoals;
  const assessmentAssignedId = data?.assesment?.id;
  const reportQuestions = data?.assesmentData?.reportQuestions;
  const reportAnswers = data?.assesmentData?.reportAnswers;

  const caseDiscipline = data?.clientPendingCaseload?.clientCaseloadTherapist?.caseDiscipline;
  const caseTherapist = data?.clientPendingCaseload?.clientCaseloadTherapist?.clientCaseloadMapping?.find(x => x.caseTherapist?.therapist_detail?.type == "veteran")?.caseTherapist;
  const therapistDetails = { caseDiscipline, caseTherapist }

  useEffect(() => {
    refetch();
  }, [refetch])
  // console.log('treatment-summary', `Treatment Summary Report_${caseDiscipline?.assign_term}_${convertToMomentDate(new Date())}.pdf `)
  const handleClickBack = () => {
    navigate(
      generatePath(TREATMENT_SUMMARY_REPORT, {
        assessmentId,
        clientId,
        clientType,
      })
    );
  }

  const handleSubmitReport = async () => {
    try {
      setSubmitting(true);
      const images = await Promise.all(
        chartRefs.current.map(async (ref) => {
          if (ref) {
            return await ref.generateImage();
          }
          return null;
        })
      );

      const doc = (
        <TreatmentSummaryReportPdfDoc
          data={data}
          images={images}
        />
      )
      const filename = `Treatment Summary Report - ${caseDiscipline?.assign_term} - ${moment(new Date()).format("YYYY-MM-DD")}.pdf`
      let pdfFile = await getPdfBlob(doc, false, filename);

      let uploadedFileKey = await uploadFile({ file: pdfFile, acl: "private", fileOriginalFormat: true }).unwrap();

      if (!uploadedFileKey) {
        throw new Error("File not uploaded");
      }

      const payload = {
        assessmentAssignedId,
        data: {
          attachment_key: uploadedFileKey,
          attachment_name: CLIENT_REPORT_TYPES[assessmentId].name,
        },
        clientType
      }

      await submitReport(payload).unwrap();
      setSubmitting(false);
      successMessageToast(REPORT_SUBMITTED_SUCCESS);
      navigate(clientListingPath(clientType));
    } catch (err) {
      setSubmitting(false);
      console.error("Error submitting report", err);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <section className="ReportSec py-3 position-relative">
        {/* {data && (
          <PDFViewer style={{ width: "100%", height: "900px" }}>
            <TreatmentSummaryReportPdfDoc
              data={data}
            />
          </PDFViewer>
        )} */}
        <Container fluid>
          <Row>
            <Col lg="12" className="my-2">
              <div className="cardCstm p-lg-5 rounded-2 bg-white shadow">
                {isLoading ? (
                  <LoadingIndicator />
                ) : isError ? (
                  <ErrorComponent />
                ) : (
                  <>
                    {isFetching && <LoadingOverlay />}
                    <HeaderPreview
                      heading={data?.assesment?.report_heading}
                    />
                    <div className="formBody pt-3">
                      <div
                        className=""
                      // style={{ borderBottom: "2px dashed #6c6c6c" }}
                      >
                        <div className="py-3">
                          <div
                            className="cardCstm rounded-2 px-3"
                            style={{ background: "#F9F9F9" }}
                          >
                            <ClientDetails
                              data={data}
                            />
                          </div>
                        </div>
                        {reportQuestions?.length > 0 &&
                          reportQuestions?.map((question, index) => {
                            if (
                              [
                                TREATMENT_SUMMARY_REPORT_QUESTIONS.notes,
                                TREATMENT_SUMMARY_REPORT_QUESTIONS.summary_clinical,
                                TREATMENT_SUMMARY_REPORT_QUESTIONS.summary_goals,
                                TREATMENT_SUMMARY_REPORT_QUESTIONS.recommendation,
                                TREATMENT_SUMMARY_REPORT_QUESTIONS.greeting,
                              ].includes(question.id)
                            ) {
                              return null;
                            }

                            let foundAnswer = reportAnswers?.length && reportAnswers.find(answer => answer.question_id === question.id);
                            return (
                              <RenderAnswer
                                key={index}
                                question={question}
                                answer={foundAnswer}
                              />
                            );
                          })
                        }
                        <TreatmentSummaryGoalsPreview
                          data={
                            reportAnswers?.length &&
                            reportAnswers.find(answer =>
                              answer.question_id === TREATMENT_SUMMARY_REPORT_QUESTIONS.treatment_summary
                            )
                          }
                          notes={
                            reportAnswers?.length &&
                            reportAnswers.find(answer =>
                              answer.question_id === TREATMENT_SUMMARY_REPORT_QUESTIONS.goal_notes
                            )
                          }
                          innerRefs={chartRefs}
                        />
                        {/* <div className="py-3">
                          <RenderAnswerByQId
                            questionId={TREATMENT_SUMMARY_REPORT_QUESTIONS.summary_goals}
                            reportQuestions={reportQuestions}
                            reportAnswers={reportAnswers}
                          />
                        </div> */}
                        <RenderAnswerByQId
                          questionId={TREATMENT_SUMMARY_REPORT_QUESTIONS.summary_clinical}
                          reportQuestions={reportQuestions}
                          reportAnswers={reportAnswers}
                        />
                        <RenderAnswerByQId
                          questionId={TREATMENT_SUMMARY_REPORT_QUESTIONS.recommendation}
                          reportQuestions={reportQuestions}
                          reportAnswers={reportAnswers}
                        />
                        <RenderAnswerByQId
                          questionId={TREATMENT_SUMMARY_REPORT_QUESTIONS.greeting}
                          reportQuestions={reportQuestions}
                          reportAnswers={reportAnswers}
                          showQuestion={false}
                        />
                      </div>
                      <div className="py-3">
                        <TherapistSignature
                          therapistDetails={therapistDetails}
                        />
                      </div>
                      <div className="py-3">
                        <KhanReportLogo />
                      </div>
                    </div>
                  </>
                )}
              </div>
            </Col>
            <Col lg="12" className="my-2 d-flex gap-10">
              <Button
                style={{
                  background: "rgb(69, 163, 165)",
                  borderColor: "rgb(69, 163, 165)",
                }}
                onClick={handleClickBack}
                disabled={!!isSubmitting || !!isSaveLater}
                className="d-flex align-items-center justify-content-center commonBtn fw-sbold"
              >
                Back
              </Button>
              {
                isVeteran && (
                  <SaveForLaterBtn
                    assessmentAssignedId={assessmentAssignedId}
                    clientType={clientType}
                    isLoading={!!isSubmitting}
                    setSaveLater={setSaveLater}
                  />
                )
              }
              {
                isVeteran && <LoadingButton
                  className="d-flex align-items-center justify-content-center commonBtn"
                  onClick={handleSubmitReport}
                  isLoading={isSubmitting}
                  disabled={!!isSaveLater}
                >
                  Submit
                </LoadingButton>
              }
            </Col>
          </Row>
        </Container>
      </section>
    </>
  );
};

export default TreatmentSummaryReportPreview;
